import { FC, useCallback, useEffect, useRef } from 'react';
import { LocaleUtils } from 'react-day-picker';
import { Calendar as PlatformCalendar, getNextDateObject, getPrevDateObject } from '@marriott/mi-ui-library';
import {
  CALENDAR_END_DATE,
  CALENDAR_SHORT_WEEKDAY_NAMES,
  CALENDAR_LONG_WEEKDAY_NAMES,
  CALENDAR_SHORT_MONTH_NAMES,
  CALENDAR_MAX_ALLOWED_DAYS,
  CALENDAR_START_DATE,
} from '../../constants';
import { getDefaultDates, getLocaleValue, getCurrentLocale, isSameMonthAndYear } from '../../utils';
import { ErrorHandlerAlert } from '../../components';
import { CalendarProps, DateObject, SelectedDateType, PlatformCalendarRef } from './Calendar.types';
import { StyledCalendar } from './Calendar.styles';

export const Calendar: FC<CalendarProps & { mode?: 'single' | 'range' }> = ({
  mode = 'range',
  variation,
  nightLabel,
  nightsLabel,
  shortWeekdayNames = CALENDAR_SHORT_WEEKDAY_NAMES,
  longWeekdayNames = CALENDAR_LONG_WEEKDAY_NAMES,
  shortMonthNames = CALENDAR_SHORT_MONTH_NAMES,
  longMonthNames = LocaleUtils.getMonths(),
  setInitialDates,
  setDates,
  selectedDateType: focusedInputDateType = CALENDAR_START_DATE,
  selectedStartDate,
  selectedEndDate,
  isDateRangeFixed = false,
  rangeStartDate,
  rangeEndDate,
  setDefaultDates,
  isCalendarClosed = true,
  isThereTwoDateInputBox = false,
  isPastDateSelectionDisabled = true,
  enableFromPastDate,
}) => {
  const isSingleMode = mode === 'single';

  const calendarRef = useRef<PlatformCalendarRef | null>(null);
  const initialStartDate = useRef<DateObject | null>(null);
  const initialEndDate = useRef<DateObject | null>(null);

  const startDate = selectedStartDate || null;
  const endDate = isSingleMode ? selectedStartDate || null : selectedEndDate || null;
  const selectedDateType = focusedInputDateType || CALENDAR_START_DATE;

  const updateView = useCallback(
    (startDate: DateObject | null, endDate: DateObject | null, shouldSetDefaultMonth = true) => {
      calendarRef.current?.setFromEndDate?.(startDate?.toDate(), endDate?.toDate());
      if (shouldSetDefaultMonth) {
        calendarRef.current?.setDefaultMonth?.(startDate?.toDate());
      }
    },
    []
  );

  useEffect(() => {
    const initialSelectedStartDate = selectedStartDate || null;
    const initialSelectedEndDate = isSingleMode ? selectedStartDate || null : selectedEndDate || null;

    const { defaultStartDate, defaultEndDate } = setDefaultDates
      ? getDefaultDates(initialSelectedStartDate, initialSelectedEndDate)
      : { defaultStartDate: initialSelectedStartDate, defaultEndDate: initialSelectedEndDate };

    const resolvedStartDate = defaultStartDate || null;
    const resolvedEndDate = (isSingleMode ? resolvedStartDate : defaultEndDate) || null;

    initialStartDate.current = isDateRangeFixed ? rangeStartDate || resolvedStartDate : resolvedStartDate;
    initialEndDate.current = isDateRangeFixed ? rangeEndDate || resolvedEndDate : resolvedEndDate;

    updateView(resolvedStartDate, resolvedEndDate);

    if (resolvedStartDate && resolvedEndDate && initialStartDate.current && initialEndDate.current) {
      setInitialDates?.(
        resolvedStartDate,
        resolvedEndDate,
        initialStartDate.current,
        initialEndDate.current,
        isSingleMode ? CALENDAR_START_DATE : selectedDateType
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const updateDates = (
    startDate: DateObject | null,
    endDate?: DateObject | null,
    selectedDateType: SelectedDateType = CALENDAR_END_DATE
  ) => {
    const selectedStartDate = startDate;
    let selectedEndDate = endDate || null;

    if (selectedStartDate?.isSame(selectedEndDate, 'date')) {
      selectedEndDate = null;
    }

    setDates(selectedStartDate, selectedEndDate, selectedDateType);
  };

  const updateStartDate = (selectedStartDate: DateObject | null) => {
    setDates(selectedStartDate, selectedStartDate, CALENDAR_START_DATE);
  };

  useEffect(() => {
    if (isCalendarClosed) {
      return;
    }

    if (!isSingleMode && startDate && !endDate) {
      return;
    }

    updateView(startDate, endDate, false);
  }, [isCalendarClosed, isSingleMode, startDate, endDate, updateView]);

  const closeDatePicker = useCallback(() => {
    if (startDate && endDate === null) {
      const isStartDateLastDateInRange = isDateRangeFixed && startDate?.isSame(initialEndDate.current, 'days');
      const selectedStartDate = isStartDateLastDateInRange ? getPrevDateObject(startDate) : startDate;
      const selectedEndDate = isStartDateLastDateInRange ? startDate : getNextDateObject(startDate);

      setDates(selectedStartDate, selectedEndDate, selectedDateType);
      updateView(selectedStartDate, selectedEndDate);
    }
  }, [startDate, endDate, isDateRangeFixed, setDates, selectedDateType, updateView]);

  useEffect(() => {
    if (isCalendarClosed && !endDate) {
      closeDatePicker();
    }
  }, [isCalendarClosed, endDate, closeDatePicker]);

  const sharedCalendarProps = {
    ref: calendarRef,
    weekdaysShort: shortWeekdayNames,
    weekdays: shortWeekdayNames,
    weekdaysLong: longWeekdayNames,
    monthsShort: shortMonthNames,
    months: longMonthNames,
    firstDayOfWeek: getLocaleValue('firstDayofWeek'),
    maxAllowedDays: CALENDAR_MAX_ALLOWED_DAYS,
    yearLabel: getLocaleValue('year') || '',
    locale: getCurrentLocale(),
    showOutsideDays: true,
  };

  const modeCalendarProps = isSingleMode
    ? {
        startDate,
        endDate,
        variation,
        focusedInput: CALENDAR_START_DATE as 'startDate',
        defaultMonth: startDate?.toDate() || initialStartDate.current?.toDate(),
        nightLabel: '',
        nightsLabel: '',
        singleDatePicker: true,
        startFromToday: true,
        disableHoverToolTip: true,
        setDateHandler: (selectedStartDate: DateObject | null) => updateStartDate(selectedStartDate),
        setFocusedInputHandler: () => undefined,
      }
    : {
        startDate,
        endDate,
        variation,
        focusedInput: selectedDateType,
        defaultMonth: initialStartDate.current?.toDate(),
        nightLabel,
        nightsLabel,
        isThereTwoDateInputBox,
        customDatesFlag: isDateRangeFixed,
        isPastDateSelectionDisabled,
        enableFromPastDate,
        ...(isDateRangeFixed && {
          customFromDate: initialStartDate.current?.toDate(),
          customToDate: initialEndDate.current?.toDate(),
          pagedNavigation: isSameMonthAndYear(startDate, initialEndDate?.current),
        }),
        setDateHandler: (selectedStartDate: DateObject | null, selectedEndDate?: DateObject | null) =>
          updateDates(selectedStartDate, selectedEndDate),
        setFocusedInputHandler: (value: SelectedDateType) => {
          if (value) {
            setDates(startDate, endDate, value);
          }
        },
      };

  const calendar = <PlatformCalendar {...sharedCalendarProps} {...modeCalendarProps} />;

  return (
    <StyledCalendar data-testid="groups-Calendar" data-component-name="m-groups-Calendar">
      <ErrorHandlerAlert>{calendar}</ErrorHandlerAlert>
    </StyledCalendar>
  );
};
